import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './Leaderboard.css';
import { SUPABASE_READY, supabase } from '../lib/supabaseClient';

const POLL_INTERVAL_MS = 60000; // refresh every 60 seconds
const DATE_FILTERS = [
  { value: 'all', label: 'All time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

function Leaderboard() {
  const [session, setSession] = useState(null);
  const [entries, setEntries] = useState([]);
  const [mostSpecies, setMostSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requiresSignIn, setRequiresSignIn] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState('');
  const [photoLoading, setPhotoLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [myCatchesOnly, setMyCatchesOnly] = useState(false);

  const viewerUserId = session?.user?.id || null;

  useEffect(() => {
    if (!SUPABASE_READY) return undefined;

    let mounted = true;

    async function initSession() {
      const sessionResponse = await supabase.auth.getSession();
      if (mounted) setSession(sessionResponse?.data?.session || null);
    }

    initSession();
    const authStateResponse = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession || null);
    });
    const subscription = authStateResponse?.data?.subscription;

    return () => {
      mounted = false;
      subscription?.unsubscribe?.();
    };
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    if (!SUPABASE_READY) {
      setError('Supabase env vars are not configured yet.');
      setLoading(false);
      return;
    }

    try {
      setRequiresSignIn(false);
      const { data, error: dbError } = await supabase
        .from('catches')
        .select('id, category, species, length_inches, description, photo_path, submitted_at, user_id, profiles!inner(display_name, username)')
        .order('submitted_at', { ascending: false });

      if (dbError) throw dbError;

      const cleaned = data || [];
      setEntries(cleaned);
      setLastUpdated(new Date());
      setError('');
    } catch (err) {
      const message = String(err?.message || '').toLowerCase();
      const isAuthRestricted =
        message.includes('permission denied') ||
        message.includes('row-level security') ||
        message.includes('jwt');

      if (isAuthRestricted) {
        setRequiresSignIn(true);
        setError('Sign in is currently required to view the leaderboard.');
      } else {
        setError('Could not load leaderboard data. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const speciesOptions = useMemo(() => {
    const sorted = [...new Set(entries.map((entry) => (entry.species || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
    return ['all', ...sorted];
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const now = Date.now();
    return entries.filter((entry) => {
      if (categoryFilter !== 'all' && entry.category !== categoryFilter) return false;
      if (speciesFilter !== 'all' && entry.species !== speciesFilter) return false;
      if (myCatchesOnly && viewerUserId && entry.user_id !== viewerUserId) return false;

      if (dateFilter !== 'all') {
        const days = Number(dateFilter);
        const submittedMs = new Date(entry.submitted_at).getTime();
        if (!Number.isFinite(submittedMs)) return false;
        if (submittedMs < now - days * 24 * 60 * 60 * 1000) return false;
      }

      return true;
    });
  }, [entries, categoryFilter, speciesFilter, dateFilter, myCatchesOnly, viewerUserId]);

  useEffect(() => {
    if (myCatchesOnly && !viewerUserId) {
      setMyCatchesOnly(false);
    }
  }, [myCatchesOnly, viewerUserId]);

  useEffect(() => {
    // Calculate "Most Species Caught" by unique species per user against the active filtered dataset.
    const byUser = {};
    filteredEntries.forEach((entry) => {
      const key = entry.user_id;
      if (!byUser[key]) {
        byUser[key] = {
          user_id: entry.user_id,
          name: entry.profiles.display_name,
          username: entry.profiles.username,
          speciesSet: new Set(),
          speciesDisplay: new Set(),
        };
      }
      const normalized = (entry.species || '').trim().toLowerCase();
      if (normalized) {
        byUser[key].speciesSet.add(normalized);
        byUser[key].speciesDisplay.add((entry.species || '').trim());
      }
    });

    const speciesLeaders = Object.values(byUser)
      .map((row) => ({
        ...row,
        speciesCount: row.speciesSet.size,
        speciesList: [...row.speciesDisplay].sort().join(', '),
      }))
      .sort((a, b) => b.speciesCount - a.speciesCount)
      .slice(0, 5);

    setMostSpecies(speciesLeaders);
  }, [filteredEntries]);

  const largestRows = useMemo(
    () =>
      filteredEntries
        .filter((entry) => entry.category === 'Largest Fish by Length')
        .sort((a, b) => Number(b.length_inches) - Number(a.length_inches))
        .slice(0, 5),
    [filteredEntries]
  );

  const smallestRows = useMemo(
    () =>
      filteredEntries
        .filter((entry) => entry.category === 'Smallest Cutest Catch')
        .sort((a, b) => Number(a.length_inches) - Number(b.length_inches))
        .slice(0, 5),
    [filteredEntries]
  );

  const uniqueRows = useMemo(
    () => filteredEntries.filter((entry) => entry.category === 'Most Unique Catch').slice(0, 5),
    [filteredEntries]
  );

  useEffect(() => {
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchLeaderboard]);

  const openEntryModal = useCallback(async (entry) => {
    setSelectedEntry(entry);
    setSelectedPhotoUrl('');
    setPhotoLoading(true);

    if (!entry.photo_path) {
      setPhotoLoading(false);
      return;
    }

    const { data, error: urlError } = await supabase.storage
      .from('catch-photos')
      .createSignedUrl(entry.photo_path, 3600);

    if (!urlError && data?.signedUrl) {
      setSelectedPhotoUrl(data.signedUrl);
    }
    setPhotoLoading(false);
  }, []);

  const closeEntryModal = useCallback(() => {
    setSelectedEntry(null);
    setSelectedPhotoUrl('');
    setPhotoLoading(false);
  }, []);

  return (
    <div className="page-container">
      <h1 className="page-title">🏆 Leaderboard</h1>
      <p className="page-subtitle">Live standings for all four derby categories.</p>

      <div className="leaderboard-meta">
        {lastUpdated && (
          <span className="last-updated">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        <button className="btn-refresh" onClick={fetchLeaderboard} aria-label="Refresh leaderboard">
          ↻ Refresh
        </button>
      </div>

      {!loading && !error && entries.length > 0 && (
        <div className="card leaderboard-filters">
          <div className="filter-grid">
            <label className="filter-field">
              <span>Category</span>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="all">All categories</option>
                <option value="Largest Fish by Length">Largest Fish by Length</option>
                <option value="Smallest Cutest Catch">Smallest Cutest Catch</option>
                <option value="Most Unique Catch">Most Unique Catch</option>
              </select>
            </label>

            <label className="filter-field">
              <span>Species</span>
              <select value={speciesFilter} onChange={(e) => setSpeciesFilter(e.target.value)}>
                {speciesOptions.map((item) => (
                  <option key={item} value={item}>
                    {item === 'all' ? 'All species' : item}
                  </option>
                ))}
              </select>
            </label>

            <label className="filter-field">
              <span>Date</span>
              <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                {DATE_FILTERS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>

            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={myCatchesOnly}
                onChange={(e) => setMyCatchesOnly(e.target.checked)}
                disabled={!viewerUserId}
              />
              <span>My catches only</span>
            </label>
          </div>
          {!viewerUserId && (
            <p className="filters-note">
              <Link to="/register" className="leaderboard-signin-link">Sign in</Link> to filter by your catches.
            </p>
          )}
        </div>
      )}

      {loading && (
        <div className="card loading-card">
          <p>Loading leaderboard...</p>
        </div>
      )}

      {error && !loading && (
        <div className="card error-card">
          <p>{error}</p>
          {requiresSignIn && (
            <p>
              <Link to="/register" className="leaderboard-signin-link">Sign in to view leaderboard</Link>
            </p>
          )}
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div className="card empty-card">
          <span className="empty-icon">🎣</span>
          <p>No catches recorded yet — the derby hasn't started! Check back on event day.</p>
        </div>
      )}

      {!loading && !error && entries.length > 0 && filteredEntries.length === 0 && (
        <div className="card empty-card">
          <span className="empty-icon">🔎</span>
          <p>No catches match the selected filters. Try broadening your search.</p>
        </div>
      )}

      {!loading && !error && filteredEntries.length > 0 && (
        <>
          <CategoryTable
            title="Largest Fish by Length"
            rows={largestRows}
            onRowClick={openEntryModal}
          />

          <CategoryTable
            title="Smallest Cutest Catch"
            rows={smallestRows}
            onRowClick={openEntryModal}
          />

          <CategoryTable
            title="Most Unique Catch"
            rows={uniqueRows}
            onRowClick={openEntryModal}
          />

          <div className="card leaderboard-card">
            <h3 className="category-title">Most Species Caught</h3>
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Angler</th>
                  <th>Unique Species</th>
                  <th className="species-list-col">Species Caught</th>
                </tr>
              </thead>
              <tbody>
                {mostSpecies.map((row, i) => (
                  <tr key={row.user_id}>
                    <td className="rank-num">{i + 1}</td>
                    <td className="name-cell">{row.name}</td>
                    <td className="weight-cell">{row.speciesCount}</td>
                    <td className="species-list-cell">{row.speciesList}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="leaderboard-mobile-list">
              {mostSpecies.map((row, i) => (
                <article key={row.user_id} className="leaderboard-mobile-card">
                  <div className="mobile-card-head">
                    <span className="mobile-rank">#{i + 1}</span>
                    <strong>{row.name}</strong>
                  </div>
                  <p className="mobile-meta">
                    Unique species: <strong>{row.speciesCount}</strong>
                  </p>
                  <p className="mobile-meta">Species: {row.speciesList || 'None yet'}</p>
                </article>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="card leaderboard-note">
        <h3>How the leaderboard works</h3>
        <p>
          Standings are grouped by category. Most Species Caught is calculated automatically
          by counting each angler's unique species submissions across the full derby.
          Results refresh every minute. Click a submission row to view photo and full details.
        </p>
      </div>

      {selectedEntry && (
        <div className="entry-modal-backdrop" role="dialog" aria-modal="true">
          <div className="entry-modal">
            <div className="entry-modal-header">
              <h3>Submission Details</h3>
              <button className="entry-modal-close" onClick={closeEntryModal} aria-label="Close details">
                ×
              </button>
            </div>

            <div className="entry-modal-content">
              <div className="entry-photo-wrap">
                {photoLoading ? (
                  <p className="entry-photo-loading">Loading photo...</p>
                ) : selectedPhotoUrl ? (
                  <img src={selectedPhotoUrl} alt="Submitted catch" className="entry-photo" />
                ) : (
                  <p className="entry-photo-loading">Photo unavailable.</p>
                )}
              </div>

              <div className="entry-details">
                <p><strong>Angler:</strong> {selectedEntry.profiles.display_name} (@{selectedEntry.profiles.username})</p>
                <p><strong>Category:</strong> {selectedEntry.category}</p>
                <p><strong>Species:</strong> {selectedEntry.species}</p>
                <p><strong>Length:</strong> {Number(selectedEntry.length_inches).toFixed(1)} in</p>
                <p><strong>Submitted:</strong> {new Date(selectedEntry.submitted_at).toLocaleString()}</p>
                <p><strong>Description:</strong> {selectedEntry.description || 'No description provided.'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryTable({ title, rows, onRowClick }) {
  return (
    <div className="card leaderboard-card">
      <h3 className="category-title">{title}</h3>
      {rows.length === 0 ? (
        <p className="category-empty">No submissions yet.</p>
      ) : (
        <>
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Angler</th>
                <th>Species</th>
                <th>Length</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry, i) => (
                <tr key={entry.id} className="clickable-row" onClick={() => onRowClick(entry)}>
                  <td className="rank-num">{i + 1}</td>
                  <td className="name-cell">{entry.profiles.display_name}</td>
                  <td>{entry.species}</td>
                  <td className="weight-cell">{Number(entry.length_inches).toFixed(1)} in</td>
                  <td className="time-cell">{new Date(entry.submitted_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="leaderboard-mobile-list">
            {rows.map((entry, i) => (
              <button key={entry.id} type="button" className="leaderboard-mobile-card" onClick={() => onRowClick(entry)}>
                <div className="mobile-card-head">
                  <span className="mobile-rank">#{i + 1}</span>
                  <strong>{entry.profiles.display_name}</strong>
                </div>
                <p className="mobile-meta">Species: {entry.species}</p>
                <p className="mobile-meta">Length: {Number(entry.length_inches).toFixed(1)} in</p>
                <p className="mobile-meta">Submitted: {new Date(entry.submitted_at).toLocaleDateString()}</p>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Leaderboard;
